<template>
  <div class="spin-game-container">
    <transition name="fade" mode="out-in">
      <!-- ========== STAGE 1: SPIN WHEEL (หน้าแรก) ========== -->
      <div v-if="stage === 'spin'" key="spin" class="stage stage-spin">
        <div class="bg-stars"></div>
        <div class="bg-radial"></div>

        <div class="spin-header">
          <h2 class="game-heading">
            <span class="heading-top">สุ่มแจกเครดิต</span>
            <span class="heading-mid animate-mawin-heading">LUCKY MAWIN</span>
            <span class="heading-bot">ให้โชค!</span>
          </h2>
          <p v-if="couponCode" class="spin-code">CODE: {{ couponCode }}</p>
        </div>

        <div class="wheel-wrapper">
          <div class="wheel-outer">
            <div class="wheel-bulbs">
              <span
                v-for="i in 24"
                :key="i"
                class="bulb"
                :class="{ on: bulbState[i % 2] }"
                :style="bulbStyle(i)"
              ></span>
            </div>

            <div class="wheel-pointer">
              <div class="pointer-dot"></div>
            </div>

            <div
              class="wheel-inner"
              :style="{
                transform: `rotate(${wheelRotation}deg)`,
                transition: isSpinning
                  ? 'transform 5s cubic-bezier(0.17, 0.67, 0.12, 0.99)'
                  : 'none',
              }"
            >
              <svg viewBox="-210 -210 420 420" class="wheel-svg">
                <defs>
                  <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.4" />
                  </filter>
                </defs>
                <g>
                  <path
                    v-for="(sector, idx) in wheelSectors"
                    :key="idx"
                    :d="sector.path"
                    :fill="sector.color"
                    stroke="#7a0000"
                    stroke-width="2"
                  />
                  <text
                    v-for="(sector, idx) in wheelSectors"
                    :key="'t' + idx"
                    :x="sector.textX"
                    :y="sector.textY"
                    :transform="`rotate(${sector.textRotDeg} ${sector.textX} ${sector.textY})`"
                    :text-anchor="sector.textAnchor"
                    dominant-baseline="middle"
                    class="sector-text"
                  >
                    {{ sector.label }}
                  </text>
                </g>
                <circle cx="0" cy="0" r="205" fill="none" stroke="#8b0000" stroke-width="8" />
                <circle cx="0" cy="0" r="198" fill="none" stroke="#ffd700" stroke-width="4" />
              </svg>
            </div>

            <div class="wheel-center">
              <div class="center-gem"></div>
            </div>
          </div>
        </div>

        <div class="code-section">
          <div class="input-wrap">
            <input
              v-model="couponCode"
              type="text"
              placeholder="กรอก CODE ของคุณ"
              class="code-input code-input-spin"
              @keyup.enter="spinTheWheel"
              maxlength="20"
            />
          </div>

          <button
            class="btn-spin"
            :class="{
              spinning: isSpinning,
              disabled: isSpinning || isChecking || !couponCode,
            }"
            @click="spinTheWheel"
          >
            <span class="btn-spin-inner">
              <span v-if="isChecking" class="spinner-inline"></span>
              <span v-else-if="isSpinning">กำลังหมุน...</span>
              <span v-else>หมุนรางวัล!</span>
            </span>
          </button>
        </div>
      </div>

      <!-- ========== STAGE 2: RESULT ========== -->
      <div v-else-if="stage === 'result'" key="result" class="stage stage-result">
        <div class="result-bg"></div>

        <!-- Sunburst rays behind trophy -->
        <div class="sunburst-wrap">
          <div class="sunburst"></div>
        </div>

        <!-- Floating stars/sparkles -->
        <div class="sparkle-layer">
          <span v-for="i in 20" :key="'sp' + i" class="sparkle" :style="sparkleStyle(i)">✦</span>
        </div>

        <canvas id="confetti" class="confetti-canvas"></canvas>

        <div class="result-content animate-result-in">
          <div class="trophy-wrap">
            <div class="trophy-glow"></div>
            <div class="result-icon">
              <svg viewBox="0 0 200 200" class="trophy-icon">
                <defs>
                  <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#fffacd" />
                    <stop offset="30%" stop-color="#ffef7a" />
                    <stop offset="55%" stop-color="#ffd700" />
                    <stop offset="85%" stop-color="#d4a000" />
                    <stop offset="100%" stop-color="#8b6508" />
                  </linearGradient>
                  <linearGradient id="goldGrad2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#fff8b0" />
                    <stop offset="40%" stop-color="#ffd700" />
                    <stop offset="100%" stop-color="#a87800" />
                  </linearGradient>
                  <linearGradient id="rimGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#ffffff" />
                    <stop offset="40%" stop-color="#fff5a0" />
                    <stop offset="100%" stop-color="#c9960a" />
                  </linearGradient>
                  <linearGradient id="baseGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#b8732d" />
                    <stop offset="50%" stop-color="#6b3c12" />
                    <stop offset="100%" stop-color="#3d1f06" />
                  </linearGradient>
                  <radialGradient id="shineGrad" cx="30%" cy="30%" r="60%">
                    <stop offset="0%" stop-color="rgba(255,255,255,0.9)" />
                    <stop offset="60%" stop-color="rgba(255,255,255,0)" />
                  </radialGradient>
                  <filter id="glowF">
                    <feGaussianBlur stdDeviation="1.2" result="b" />
                    <feMerge>
                      <feMergeNode in="b" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                <!-- Base bottom board -->
                <ellipse cx="100" cy="188" rx="68" ry="10" fill="rgba(0,0,0,0.5)" />
                <rect
                  x="58"
                  y="164"
                  width="84"
                  height="22"
                  rx="5"
                  fill="url(#baseGrad)"
                  stroke="#3d1f06"
                  stroke-width="2"
                />
                <rect
                  x="68"
                  y="148"
                  width="64"
                  height="18"
                  rx="4"
                  fill="url(#goldGrad2)"
                  stroke="#8b6508"
                  stroke-width="2"
                />
                <rect
                  x="88"
                  y="128"
                  width="24"
                  height="24"
                  fill="url(#goldGrad2)"
                  stroke="#8b6508"
                  stroke-width="2"
                />

                <!-- Trophy cup body -->
                <path
                  d="M52 42 L58 108 Q60 128 100 132 Q140 128 142 108 L148 42 Z"
                  fill="url(#goldGrad)"
                  stroke="#8b6508"
                  stroke-width="2.5"
                  filter="url(#glowF)"
                />
                <!-- Shine highlight -->
                <path
                  d="M64 48 Q62 74 68 110 Q72 118 78 122"
                  fill="none"
                  stroke="url(#shineGrad)"
                  stroke-width="10"
                  stroke-linecap="round"
                  opacity="0.75"
                />

                <!-- Top rim -->
                <ellipse
                  cx="100"
                  cy="42"
                  rx="50"
                  ry="9"
                  fill="url(#rimGrad)"
                  stroke="#8b6508"
                  stroke-width="2"
                />
                <ellipse cx="100" cy="40" rx="42" ry="6" fill="#5a3c00" opacity="0.5" />
                <!-- Rim inner glow -->
                <ellipse
                  cx="100"
                  cy="43"
                  rx="44"
                  ry="4"
                  fill="none"
                  stroke="#fff8b0"
                  stroke-width="2"
                  opacity="0.7"
                />

                <!-- Left handle -->
                <path
                  d="M52 50 Q20 48 20 78 Q20 106 56 104"
                  fill="none"
                  stroke="url(#goldGrad2)"
                  stroke-width="10"
                  stroke-linecap="round"
                />
                <path
                  d="M52 50 Q20 48 20 78 Q20 106 56 104"
                  fill="none"
                  stroke="#8b6508"
                  stroke-width="2"
                  stroke-linecap="round"
                  opacity="0.8"
                />

                <!-- Right handle -->
                <path
                  d="M148 50 Q180 48 180 78 Q180 106 144 104"
                  fill="none"
                  stroke="url(#goldGrad2)"
                  stroke-width="10"
                  stroke-linecap="round"
                />
                <path
                  d="M148 50 Q180 48 180 78 Q180 106 144 104"
                  fill="none"
                  stroke="#8b6508"
                  stroke-width="2"
                  stroke-linecap="round"
                  opacity="0.8"
                />

                <!-- Decorative star / emblem on cup -->
                <path
                  d="M100 70 L104 84 L119 84 L107 93 L111 107 L100 98 L89 107 L93 93 L81 84 L96 84 Z"
                  fill="url(#goldGrad2)"
                  stroke="#8b6508"
                  stroke-width="1.2"
                  filter="url(#glowF)"
                />
                <!-- Second small star -->
                <path
                  d="M72 74 L73.5 79 L79 79 L74.5 82 L76 87 L72 84 L68 87 L69.5 82 L65 79 L70.5 79 Z"
                  fill="url(#goldGrad2)"
                  stroke="#8b6508"
                  stroke-width="0.9"
                  opacity="0.9"
                />
                <path
                  d="M128 74 L129.5 79 L135 79 L130.5 82 L132 87 L128 84 L124 87 L125.5 82 L121 79 L126.5 79 Z"
                  fill="url(#goldGrad2)"
                  stroke="#8b6508"
                  stroke-width="0.9"
                  opacity="0.9"
                />

                <!-- Front gem / diamond shape -->
                <path
                  d="M100 102 L110 112 L100 122 L90 112 Z"
                  fill="#ff0040"
                  stroke="#8b0020"
                  stroke-width="1.2"
                  filter="url(#glowF)"
                />
                <path d="M100 102 L105 112 L100 115 Z" fill="rgba(255,255,255,0.5)" />
              </svg>
            </div>
          </div>

          <h2 class="result-congrats">ยินดีด้วย!</h2>
          <p class="result-subtitle">คุณได้รับรางวัล</p>

          <div class="reward-amount-wrap">
            <span class="reward-amount">{{ rewardAmount }}</span>
            <span class="reward-unit">฿</span>
          </div>

          <div class="result-actions">
            <button class="btn-play-again" @click="resetGame">เล่นอีกครั้ง</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import axios from 'axios';
import { API_PATHS } from '@/utils/ApiPath.js';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const SECTOR_COLORS = [
  '#8ed6ff',
  '#ffc15e',
  '#ff9edb',
  '#4ec5ff',
  '#6eff9e',
  '#ffb56b',
  '#e0aaff',
  '#b7f0ff',
  '#5ed6a0',
  '#ffaa7a',
  '#c29bff',
  '#ffd79b',
];

function buildSectors(items) {
  const n = items.length;
  const radius = 195;
  const anglePer = (2 * Math.PI) / n;
  const startAngle = -Math.PI / 2 - anglePer / 2;
  const textR = radius * 0.45;

  return items.map((item, i) => {
    const a0 = startAngle + i * anglePer;
    const a1 = a0 + anglePer;
    const x0 = Math.cos(a0) * radius;
    const y0 = Math.sin(a0) * radius;
    const x1 = Math.cos(a1) * radius;
    const y1 = Math.sin(a1) * radius;
    const large = anglePer > Math.PI ? 1 : 0;
    const path = `M0,0 L${x0},${y0} A${radius},${radius} 0 ${large} 1 ${x1},${y1} Z`;

    const midAngle = a0 + anglePer / 2;
    const textX = Math.cos(midAngle) * textR;
    const textY = Math.sin(midAngle) * textR;

    const baseRotDeg = midAngle * (180 / Math.PI);
    const norm = ((baseRotDeg % 360) + 360) % 360;
    const flipped = norm >= 90 && norm <= 270;
    const textRotDeg = flipped ? baseRotDeg + 180 : baseRotDeg;
    const textAnchor = flipped ? 'end' : 'start';

    return {
      path,
      textX,
      textY,
      textRotDeg,
      textAnchor,
      color: SECTOR_COLORS[i % SECTOR_COLORS.length],
      label: item.label,
      amount: item.amount,
      id: item.id,
    };
  });
}

export default {
  name: 'SpinGame',
  setup() {
    const stage = ref('spin');
    const couponCode = ref('');
    const isChecking = ref(false);
    const isSpinning = ref(false);
    const wheelRotation = ref(0);
    const rewardAmount = ref(0);
    const spinRewards = ref([]);
    const bulbState = ref({ 0: true, 1: false });

    const wheelSectors = computed(() => buildSectors(spinRewards.value));

    const translateErrorMessage = (msg) => {
      if (!msg) return 'โค้ดนี้ไม่สามารถใช้ได้';
      if (msg === 'Code already used') return 'โค้ดนี้ถูกใช้ไปแล้ว';
      if (msg === 'Code not found') return 'ไม่พบโค้ด';
      return msg;
    };

    const fallbackRewards = [
      { id: 1, label: 'ไม่ได้รางวัล', amount: 0 },
      { id: 2, label: '20 เครดิต', amount: 20 },
      { id: 3, label: '30 เครดิต', amount: 30 },
      { id: 4, label: '50 เครดิต', amount: 50 },
      { id: 5, label: '70 เครดิต', amount: 70 },
      { id: 6, label: '100 เครดิต', amount: 100 },
      { id: 7, label: '200 เครดิต', amount: 200 },
      { id: 8, label: '300 เครดิต', amount: 300 },
      { id: 9, label: '500 เครดิต', amount: 500 },
      { id: 10, label: '1000 เครดิต', amount: 1000 },
    ];

    const fetchSpinRewards = async () => {
      try {
        const res = await axios.get(API_PATHS.GET_REWARD);
        if (res.data && Array.isArray(res.data) && res.data.length >= 1) {
          spinRewards.value = res.data.map((it, i) => ({
            id: it._id ?? it.id ?? i + 1,
            label: String(it.label ?? it.amount ?? ''),
            amount: Number(it.amount ?? 0),
          }));
        } else {
          spinRewards.value = [...fallbackRewards];
        }
      } catch (e) {
        spinRewards.value = [...fallbackRewards];
      }
    };

    const redeemCouponCode = async () => {
      try {
        const response = await axios.post(API_PATHS.USE_CODE, { code: couponCode.value });
        rewardAmount.value = response.data.reward.amount;
        return true;
      } catch (error) {
        console.error('Error redeeming:', error);
        const errMsg = translateErrorMessage(error.response?.data?.message);
        Swal.fire({
          icon: 'error',
          title: 'ไม่สามารถใช้โค้ดได้',
          text: errMsg,
          confirmButtonText: 'ตกลง',
        });
        return false;
      }
    };

    const spinTheWheel = async () => {
      if (isSpinning.value || isChecking.value) return;
      if (!couponCode.value) {
        Swal.fire({ icon: 'warning', title: 'กรุณากรอก CODE ก่อน', confirmButtonText: 'ตกลง' });
        return;
      }

      isChecking.value = true;
      try {
        await axios.post(API_PATHS.CHECK_CODE, { code: couponCode.value });
      } catch (error) {
        const errMsg = translateErrorMessage(error.response?.data?.message);
        Swal.fire({ icon: 'warning', title: errMsg, confirmButtonText: 'ตกลง' });
        isChecking.value = false;
        return;
      }
      isChecking.value = false;

      isSpinning.value = true;

      const success = await redeemCouponCode();
      if (!success) {
        isSpinning.value = false;
        return;
      }

      const sectors = wheelSectors.value;
      const n = sectors.length;
      const anglePer = 360 / n;

      let targetIdx = sectors.findIndex((s) => Number(s.amount) === Number(rewardAmount.value));
      if (targetIdx === -1) targetIdx = Math.floor(Math.random() * n);

      const sectorMidAngle = targetIdx * anglePer;
      const jitter = (Math.random() - 0.5) * (anglePer * 0.6);
      const baseTurns = 6;
      const currentMod = ((wheelRotation.value % 360) + 360) % 360;
      const targetNormalized = (360 - sectorMidAngle + jitter + 360) % 360;
      let delta = targetNormalized - currentMod;
      if (delta <= 0) delta += 360;
      const finalRotation = wheelRotation.value + baseTurns * 360 + delta;

      wheelRotation.value = finalRotation;

      setTimeout(() => {
        isSpinning.value = false;
        stage.value = 'result';
        nextTick(() => initConfetti());
      }, 5200);
    };

    const resetGame = () => {
      couponCode.value = '';
      rewardAmount.value = 0;
      wheelRotation.value = 0;
      stage.value = 'spin';
    };

    let bulbTimer = null;
    const startBulbs = () => {
      bulbTimer = setInterval(() => {
        bulbState.value = { 0: !bulbState.value[0], 1: !bulbState.value[1] };
      }, 350);
    };

    const initConfetti = () => {
      const canvas = document.getElementById('confetti');
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const confettis = [];
      const colors = [
        '#FF007A',
        '#7A00FF',
        '#00FF7A',
        '#FFD700',
        '#00D4FF',
        '#FF4D4D',
        '#FFB800',
        '#FF9EDB',
        '#8ED6FF',
        '#6EFF9E',
      ];
      const shapes = ['rect', 'circle', 'coin', 'tri'];

      const create = () => {
        const shape = shapes[Math.floor(Math.random() * shapes.length)];
        return {
          x: Math.random() * canvas.width,
          y: -30 - Math.random() * 120,
          size: Math.random() * 14 + 6,
          color: colors[Math.floor(Math.random() * colors.length)],
          speedX: (Math.random() - 0.5) * 4,
          speedY: Math.random() * 6 + 3,
          rotation: Math.random() * 360,
          rotSpeed: (Math.random() - 0.5) * 14,
          wobblePhase: Math.random() * Math.PI * 2,
          wobbleAmp: Math.random() * 2 + 1,
          shape,
        };
      };

      for (let i = 0; i < 450; i++) confettis.push(create());

      const drawCoin = (x, y, size, rot) => {
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate((rot * Math.PI) / 180);
        const grd = ctx.createRadialGradient(-size / 4, -size / 4, size / 8, 0, 0, size);
        grd.addColorStop(0, '#fff8b0');
        grd.addColorStop(0.5, '#ffd700');
        grd.addColorStop(1, '#a87800');
        ctx.fillStyle = grd;
        ctx.beginPath();
        ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#8b6508';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.fillStyle = '#8b6508';
        ctx.font = `bold ${Math.floor(size * 0.55)}px Impact`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('฿', 0, 1);
        ctx.restore();
      };

      const loop = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        confettis.forEach((c, idx) => {
          c.wobblePhase += 0.1;
          c.x += c.speedX + Math.sin(c.wobblePhase) * c.wobbleAmp;
          c.y += c.speedY;
          c.rotation += c.rotSpeed;
          ctx.save();
          ctx.translate(c.x, c.y);
          ctx.rotate((c.rotation * Math.PI) / 180);
          ctx.fillStyle = c.color;
          if (c.shape === 'rect') {
            ctx.fillRect(-c.size / 2, -c.size / 2, c.size, c.size);
          } else if (c.shape === 'tri') {
            ctx.beginPath();
            ctx.moveTo(0, -c.size / 2);
            ctx.lineTo(c.size / 2, c.size / 2);
            ctx.lineTo(-c.size / 2, c.size / 2);
            ctx.closePath();
            ctx.fill();
          } else if (c.shape === 'coin') {
            ctx.restore();
            drawCoin(c.x, c.y, c.size, c.rotation);
            if (c.y > canvas.height + 60) confettis.splice(idx, 1);
            return;
          } else {
            ctx.beginPath();
            ctx.arc(0, 0, c.size / 2, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.restore();
          if (c.y > canvas.height + 60) confettis.splice(idx, 1);
        });
        if (confettis.length > 0) requestAnimationFrame(loop);
      };
      loop();
    };

    const sparkleStyle = (i) => {
      const left = Math.random() * 100;
      const top = Math.random() * 100;
      const delay = Math.random() * 2.2;
      const dur = 1.2 + Math.random() * 2.2;
      const size = 14 + Math.random() * 22;
      const colors = ['#FFD700', '#FFF5A0', '#FFFFFF', '#FFB800', '#FFF07A'];
      return {
        left: `${left}%`,
        top: `${top}%`,
        animationDelay: `${delay}s`,
        animationDuration: `${dur}s`,
        fontSize: `${size}px`,
        color: colors[i % colors.length],
        textShadow: `0 0 8px ${colors[i % colors.length]}, 0 0 16px ${colors[i % colors.length]}`,
      };
    };

    const bulbStyle = (i) => {
      const angle = (i / 24) * 360;
      return { transform: `rotate(${angle}deg) translateY(-182px)` };
    };

    onMounted(() => {
      fetchSpinRewards();
      startBulbs();
      window.addEventListener('resize', () => {
        const c = document.getElementById('confetti');
        if (c) {
          c.width = window.innerWidth;
          c.height = window.innerHeight;
        }
      });
    });

    onUnmounted(() => {
      if (bulbTimer) clearInterval(bulbTimer);
    });

    return {
      stage,
      couponCode,
      isChecking,
      isSpinning,
      wheelRotation,
      wheelSectors,
      rewardAmount,
      bulbState,
      spinTheWheel,
      resetGame,
      sparkleStyle,
      bulbStyle,
    };
  },
};
</script>

<style scoped>
.spin-game-container {
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
  font-family: 'Prompt', 'Kanit', sans-serif;
  background: #1a0022;
}

.stage {
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

/* ============== FADE TRANSITION ============== */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.45s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ============== STARS BG ============== */
.bg-stars {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(2px 2px at 20% 30%, #ffffff, transparent),
    radial-gradient(2px 2px at 60% 70%, #ffd700, transparent),
    radial-gradient(1.5px 1.5px at 50% 50%, #ffffff, transparent),
    radial-gradient(2px 2px at 80% 10%, #ff9edb, transparent),
    radial-gradient(1.5px 1.5px at 15% 80%, #8ed6ff, transparent),
    radial-gradient(2px 2px at 90% 60%, #ffffff, transparent),
    radial-gradient(1.5px 1.5px at 35% 15%, #ffd700, transparent),
    radial-gradient(2px 2px at 70% 40%, #ffffff, transparent);
  background-size: 200% 200%;
  animation: starsMove 18s linear infinite;
  opacity: 0.7;
}
@keyframes starsMove {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 400px 400px;
  }
}

.bg-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 20% 20%, rgba(255, 77, 77, 0.25), transparent 55%),
    radial-gradient(ellipse at 80% 80%, rgba(255, 158, 219, 0.25), transparent 55%),
    radial-gradient(ellipse at 50% 100%, rgba(120, 0, 0, 0.7), transparent 65%),
    linear-gradient(180deg, #200018 0%, #4a0000 55%, #1a0000 100%);
}

.bg-radial {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 30%, rgba(255, 215, 0, 0.25), transparent 55%),
    radial-gradient(ellipse at 50% 100%, rgba(120, 0, 0, 0.75), transparent 60%),
    linear-gradient(180deg, #280015 0%, #5a0010 50%, #1a0000 100%);
}

/* ============== CODE INPUT ============== */
.code-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

.logo-wrap {
  position: relative;
  margin-bottom: 40px;
  text-align: center;
}

.logo-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 320px;
  height: 320px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 2px solid transparent;
  background: linear-gradient(#1a0000, #1a0000) padding-box,
    conic-gradient(from 0deg, #ffd700, #ff4d4d, #ffd700, #ff9edb, #ffd700) border-box;
  opacity: 0.4;
  animation: ringPulse 3s ease-in-out infinite;
  filter: blur(2px);
}
@keyframes ringPulse {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.35;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.08);
    opacity: 0.6;
  }
}

.game-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin: 0;
}
.title-top,
.title-bot {
  color: #ffd700;
  font-size: 1.9rem;
  font-weight: 800;
  text-shadow: 0 0 8px rgba(255, 215, 0, 0.7), 0 0 16px rgba(255, 140, 0, 0.6),
    0 3px 6px rgba(0, 0, 0, 0.9);
  letter-spacing: 1px;
}
.title-mid {
  font-size: 3.2rem;
  font-weight: 900;
  letter-spacing: 6px;
  background: linear-gradient(180deg, #fff5a0 0%, #ffd700 45%, #ff7b00 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.7));
}
.animate-phoenix {
  animation: phoenixPulse 2s ease-in-out infinite;
}
@keyframes phoenixPulse {
  0%,
  100% {
    transform: scale(1);
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.7));
  }
  50% {
    transform: scale(1.08);
    filter: drop-shadow(0 0 12px rgba(255, 215, 0, 0.9))
      drop-shadow(0 0 24px rgba(255, 120, 0, 0.7)) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.7));
  }
}

.code-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.input-wrap {
  position: relative;
  width: 320px;
}
.code-input {
  width: 100%;
  padding: 16px 20px;
  font-size: 1.15rem;
  font-weight: 700;
  text-align: center;
  letter-spacing: 2px;
  color: #4a0000;
  background: linear-gradient(180deg, #fffbe8 0%, #fff1b8 100%);
  border: 3px solid #ffd700;
  border-radius: 14px;
  outline: none;
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.15), 0 0 16px rgba(255, 215, 0, 0.5),
    0 6px 16px rgba(0, 0, 0, 0.5);
  transition: all 0.25s ease;
  text-transform: uppercase;
}
.code-input::placeholder {
  color: #b89a3a;
  letter-spacing: 1px;
  font-weight: 500;
}
.code-input:focus {
  border-color: #ff9500;
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.15), 0 0 24px rgba(255, 150, 0, 0.75),
    0 8px 20px rgba(0, 0, 0, 0.5);
  transform: translateY(-1px);
}

.btn-submit {
  position: relative;
  padding: 14px 54px;
  font-size: 1.4rem;
  font-weight: 900;
  letter-spacing: 3px;
  color: #4a0000;
  background: linear-gradient(180deg, #fff1a8 0%, #ffd700 50%, #c99400 100%);
  border: 3px solid #8b6508;
  border-radius: 14px;
  cursor: pointer;
  box-shadow: inset 0 2px 0 rgba(255, 255, 255, 0.6), inset 0 -4px 0 rgba(0, 0, 0, 0.2),
    0 0 20px rgba(255, 215, 0, 0.6), 0 8px 0 #5a3d00, 0 12px 24px rgba(0, 0, 0, 0.55);
  transition: all 0.12s ease;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
}
.btn-submit:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: inset 0 2px 0 rgba(255, 255, 255, 0.6), inset 0 -4px 0 rgba(0, 0, 0, 0.2),
    0 0 28px rgba(255, 215, 0, 0.85), 0 10px 0 #5a3d00, 0 14px 28px rgba(0, 0, 0, 0.55);
}
.btn-submit:active:not(.disabled) {
  transform: translateY(5px);
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.35), 0 0 16px rgba(255, 215, 0, 0.5), 0 3px 0 #5a3d00,
    0 4px 10px rgba(0, 0, 0, 0.5);
}
.btn-submit.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  filter: grayscale(0.3);
}

.spinner-mini {
  display: inline-block;
  width: 22px;
  height: 22px;
  border: 3px solid rgba(74, 0, 0, 0.3);
  border-top-color: #4a0000;
  border-radius: 50%;
  animation: spinMini 0.8s linear infinite;
}
.spinner-inline {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spinMini 0.8s linear infinite;
  vertical-align: middle;
  margin-right: 6px;
}
@keyframes spinMini {
  to {
    transform: rotate(360deg);
  }
}

/* ============== SPLASH ============== */
.stage-splash {
  background: radial-gradient(ellipse at center, #5a0010 0%, #1a0000 80%);
  overflow: hidden;
}
.splash-bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 50%, rgba(255, 215, 0, 0.2), transparent 60%);
  animation: splashBg 1.8s ease-in-out infinite;
}
@keyframes splashBg {
  0%,
  100% {
    opacity: 0.5;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}
.splash-particles {
  position: absolute;
  inset: 0;
}
.particle {
  position: absolute;
  top: 100%;
  border-radius: 50%;
  opacity: 0;
  animation: particleRise 1.2s ease-out forwards;
  box-shadow: 0 0 10px currentColor;
}
@keyframes particleRise {
  0% {
    top: 100%;
    opacity: 0;
    transform: scale(0.5);
  }
  20% {
    opacity: 1;
  }
  100% {
    top: -10%;
    opacity: 0;
    transform: scale(1.4);
  }
}

.splash-content {
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}
.splash-ring {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  border: 6px solid transparent;
  background: linear-gradient(#1a0000, #1a0000) padding-box,
    conic-gradient(from 0deg, #ffd700, #ff4d4d, #ff9edb, #ffd700) border-box;
  animation: splashRing 0.8s linear infinite, splashRingScale 1.5s ease-in-out infinite;
}
@keyframes splashRing {
  to {
    transform: rotate(360deg);
  }
}
@keyframes splashRingScale {
  0%,
  100% {
    box-shadow: 0 0 40px rgba(255, 215, 0, 0.6);
  }
  50% {
    box-shadow: 0 0 80px rgba(255, 215, 0, 0.95), 0 0 120px rgba(255, 80, 80, 0.5);
  }
}
.splash-title {
  font-size: 2.4rem;
  font-weight: 800;
  color: #ffd700;
  text-shadow: 0 0 12px rgba(255, 215, 0, 0.9), 0 0 22px rgba(255, 140, 0, 0.8),
    0 4px 8px rgba(0, 0, 0, 0.9);
  letter-spacing: 3px;
  margin: 0;
}
.splash-sub {
  font-size: 1.5rem;
  font-weight: 700;
  color: #ff9edb;
  text-shadow: 0 0 10px rgba(255, 158, 219, 0.8), 0 3px 6px rgba(0, 0, 0, 0.8);
  letter-spacing: 4px;
  margin: 0;
}
.animate-splash {
  animation: splashTextPulse 0.6s ease-in-out infinite alternate;
}
@keyframes splashTextPulse {
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.06);
  }
}

/* ============== SPIN WHEEL ============== */
.stage-spin {
  padding: 24px 12px 60px;
  min-height: 100vh;
}
.spin-header {
  position: relative;
  z-index: 2;
  text-align: center;
  margin-bottom: 12px;
}
.game-heading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  margin: 0 0 6px;
}
.heading-top,
.heading-bot {
  color: #ffd700;
  font-size: 1.25rem;
  font-weight: 800;
  text-shadow: 0 0 8px rgba(255, 215, 0, 0.7), 0 0 16px rgba(255, 140, 0, 0.6),
    0 3px 6px rgba(0, 0, 0, 0.9);
  letter-spacing: 1px;
}
.heading-mid {
  font-size: 2.2rem;
  font-weight: 900;
  letter-spacing: 4px;
  background: linear-gradient(180deg, #fff5a0 0%, #ffd700 45%, #ff7b00 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.7));
}
.animate-mawin-heading {
  animation: mawinHeadingPulse 2s ease-in-out infinite;
}
@keyframes mawinHeadingPulse {
  0%,
  100% {
    transform: scale(1);
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.7));
  }
  50% {
    transform: scale(1.06);
    filter: drop-shadow(0 0 10px rgba(255, 215, 0, 0.9))
      drop-shadow(0 0 20px rgba(255, 120, 0, 0.7)) drop-shadow(0 4px 8px rgba(0, 0, 0, 0.7));
  }
}
.spin-code {
  font-size: 0.95rem;
  color: #ffb866;
  letter-spacing: 3px;
  margin-top: 2px;
  font-weight: 600;
  text-shadow: 0 0 8px rgba(255, 150, 0, 0.6);
}

.wheel-wrapper {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
}
.wheel-outer {
  position: relative;
  width: min(92vw, 400px);
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  background: linear-gradient(145deg, #c90000 0%, #8b0000 50%, #5a0000 100%);
  box-shadow: inset 0 4px 0 rgba(255, 120, 120, 0.5), inset 0 -6px 0 rgba(0, 0, 0, 0.4),
    0 0 30px rgba(255, 50, 50, 0.5), 0 0 60px rgba(255, 215, 0, 0.3), 0 12px 30px rgba(0, 0, 0, 0.7);
  border: 5px solid #ffd700;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wheel-bulbs {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.bulb {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 12px;
  height: 12px;
  margin-left: -6px;
  margin-top: -6px;
  border-radius: 50%;
  background: #880000;
  transition: background 0.1s ease, box-shadow 0.1s ease;
}
.bulb.on {
  background: radial-gradient(circle at 30% 30%, #fffacd, #ffd700 60%, #c98c00);
  box-shadow: 0 0 10px #ffd700, 0 0 20px #ff9500, 0 0 30px rgba(255, 150, 0, 0.7);
}

.wheel-pointer {
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  width: 0;
  height: 0;
  border-left: 23px solid transparent;
  border-right: 23px solid transparent;
  border-top: 46px solid #ff1744;
  filter: drop-shadow(0 0 10px rgba(255, 23, 68, 0.95)) drop-shadow(0 0 20px rgba(255, 60, 60, 0.7))
    drop-shadow(0 4px 6px rgba(0, 0, 0, 0.7));
  animation: pointerPulse 1.3s ease-in-out infinite;
}
.wheel-pointer::after {
  content: '';
  position: absolute;
  top: -50px;
  left: -16px;
  width: 0;
  height: 0;
  border-left: 16px solid transparent;
  border-right: 16px solid transparent;
  border-top: 32px solid #ff5c6f;
}
@keyframes pointerPulse {
  0%,
  100% {
    filter: drop-shadow(0 0 10px rgba(255, 23, 68, 0.95))
      drop-shadow(0 0 20px rgba(255, 60, 60, 0.7)) drop-shadow(0 4px 6px rgba(0, 0, 0, 0.7));
  }
  50% {
    filter: drop-shadow(0 0 16px rgba(255, 60, 80, 1)) drop-shadow(0 0 32px rgba(255, 120, 60, 0.9))
      drop-shadow(0 4px 6px rgba(0, 0, 0, 0.7));
  }
}
.pointer-dot {
  position: absolute;
  top: -62px;
  left: -50%;
  transform: translateX(-50%);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 30% 25%,
    #ffffff 0%,
    #fff5a0 30%,
    #ffd700 60%,
    #b8860b 100%
  );
  border: 2px solid #ffb300;
  box-shadow: 0 0 12px rgba(255, 215, 0, 1), 0 0 24px rgba(255, 180, 0, 0.85),
    inset 0 -2px 4px rgba(139, 69, 0, 0.7), inset 0 2px 4px rgba(255, 255, 255, 0.85);
}

.wheel-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #1a0000;
  box-shadow: inset 0 0 30px rgba(0, 0, 0, 0.6);
  overflow: hidden;
  will-change: transform;
}
.wheel-svg {
  width: 100%;
  height: 100%;
  display: block;
}
.sector-text {
  font-size: 16px;
  font-weight: 900;
  fill: #2a0000;
  paint-order: stroke;
  stroke: rgba(255, 255, 255, 0.9);
  stroke-width: 3px;
  font-family: 'Prompt', 'Kanit', 'Sarabun', 'Impact', sans-serif;
  letter-spacing: 0.5px;
}

.wheel-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #fff6c0, #ffd700 50%, #8b6508 100%);
  border: 4px solid #8b0000;
  box-shadow: inset 0 2px 8px rgba(255, 255, 255, 0.8), inset 0 -4px 8px rgba(0, 0, 0, 0.3),
    0 0 16px rgba(255, 215, 0, 0.7), 0 4px 10px rgba(0, 0, 0, 0.6);
  z-index: 15;
  display: flex;
  align-items: center;
  justify-content: center;
}
.center-gem {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #fff, #ff4d4d 60%, #8b0000);
  box-shadow: 0 0 10px rgba(255, 0, 0, 0.7);
}

.code-section {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  margin-top: 28px;
  padding: 0 12px;
}
.code-input-spin {
  font-size: 1.1rem;
  padding: 14px 18px;
}

.btn-spin {
  position: relative;
  z-index: 2;
  padding: 16px 60px;
  font-size: 1.6rem;
  font-weight: 900;
  letter-spacing: 4px;
  color: #fff;
  background: linear-gradient(180deg, #ff6a00 0%, #e63900 50%, #8b1a00 100%);
  border: 4px solid #ffd700;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: inset 0 2px 0 rgba(255, 255, 255, 0.5), inset 0 -6px 0 rgba(0, 0, 0, 0.25),
    0 0 28px rgba(255, 100, 0, 0.7), 0 0 60px rgba(255, 215, 0, 0.5), 0 10px 0 #5a0d00,
    0 14px 30px rgba(0, 0, 0, 0.6);
  transition: all 0.12s ease;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.7), 0 0 10px rgba(255, 255, 0, 0.6);
}
.btn-spin:hover:not(.disabled) {
  transform: translateY(-3px);
  box-shadow: inset 0 2px 0 rgba(255, 255, 255, 0.5), inset 0 -6px 0 rgba(0, 0, 0, 0.25),
    0 0 40px rgba(255, 120, 0, 0.9), 0 0 80px rgba(255, 215, 0, 0.7), 0 13px 0 #5a0d00,
    0 18px 36px rgba(0, 0, 0, 0.6);
}
.btn-spin:active:not(.disabled) {
  transform: translateY(6px);
  box-shadow: inset 0 4px 10px rgba(0, 0, 0, 0.4), 0 0 20px rgba(255, 100, 0, 0.7),
    0 0 40px rgba(255, 215, 0, 0.5), 0 4px 0 #5a0d00, 0 6px 16px rgba(0, 0, 0, 0.6);
}
.btn-spin.spinning {
  animation: btnSpinPulse 0.5s ease-in-out infinite alternate;
}
.btn-spin.disabled {
  cursor: wait;
  opacity: 0.8;
}
.btn-spin-inner {
  display: inline-block;
}
@keyframes btnSpinPulse {
  from {
    filter: brightness(1);
  }
  to {
    filter: brightness(1.3);
  }
}

/* ============== RESULT ============== */
.stage-result {
  overflow: hidden;
}
.result-bg {
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 35%, rgba(255, 215, 0, 0.55), transparent 55%),
    radial-gradient(ellipse at 20% 80%, rgba(255, 80, 80, 0.3), transparent 50%),
    radial-gradient(ellipse at 80% 80%, rgba(184, 77, 255, 0.3), transparent 50%),
    radial-gradient(ellipse at 50% 15%, rgba(255, 240, 150, 0.3), transparent 45%),
    linear-gradient(180deg, #3a0020 0%, #5a0000 50%, #1a0000 100%);
  animation: resultBgPulse 3s ease-in-out infinite;
}
@keyframes resultBgPulse {
  0%,
  100% {
    filter: brightness(1);
  }
  50% {
    filter: brightness(1.15);
  }
}

/* Sunburst rays behind trophy */
.sunburst-wrap {
  position: absolute;
  top: 18%;
  left: 50%;
  width: 900px;
  height: 900px;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 2;
  overflow: hidden;
}
.sunburst {
  position: absolute;
  inset: 0;
  background: conic-gradient(
    from 0deg,
    rgba(255, 240, 120, 0) 0deg,
    rgba(255, 240, 120, 0) 8deg,
    rgba(255, 240, 120, 0.55) 10deg,
    rgba(255, 240, 120, 0) 12deg,
    rgba(255, 240, 120, 0) 26deg,
    rgba(255, 180, 50, 0.45) 28deg,
    rgba(255, 240, 120, 0) 30deg,
    rgba(255, 240, 120, 0) 40deg
  );
  background-size: 40deg 40deg;
  background-repeat: repeat;
  animation: sunburstSpin 14s linear infinite;
  filter: blur(2px);
  opacity: 0.9;
}
.sunburst::before {
  content: '';
  position: absolute;
  inset: 0;
  background: conic-gradient(
    from 5deg,
    rgba(255, 255, 255, 0) 0deg,
    rgba(255, 255, 255, 0.35) 3deg,
    rgba(255, 255, 255, 0) 6deg,
    rgba(255, 255, 255, 0) 20deg
  );
  background-size: 20deg 20deg;
  background-repeat: repeat;
  animation: sunburstSpin 8s linear infinite reverse;
  mix-blend-mode: screen;
  opacity: 0.85;
}
@keyframes sunburstSpin {
  to {
    transform: rotate(360deg);
  }
}

/* Sparkle layer */
.sparkle-layer {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 8;
  overflow: hidden;
}
.sparkle {
  position: absolute;
  display: inline-block;
  font-weight: 900;
  opacity: 0;
  transform-origin: center;
  animation: sparkleAnim ease-in-out infinite;
}
@keyframes sparkleAnim {
  0% {
    opacity: 0;
    transform: scale(0.3) rotate(0deg);
  }
  25% {
    opacity: 1;
    transform: scale(1.1) rotate(90deg);
  }
  50% {
    opacity: 0.4;
    transform: scale(0.7) rotate(180deg);
  }
  75% {
    opacity: 1;
    transform: scale(1.2) rotate(270deg);
  }
  100% {
    opacity: 0;
    transform: scale(0.2) rotate(360deg);
  }
}

.confetti-canvas {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 5;
}

.result-content {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 24px;
  max-width: 440px;
  width: 100%;
}
.trophy-wrap {
  position: relative;
  margin-bottom: 10px;
}
.trophy-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 280px;
  height: 280px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(
    circle at center,
    rgba(255, 240, 120, 0.7),
    rgba(255, 180, 50, 0.35) 40%,
    transparent 70%
  );
  animation: trophyGlowPulse 1.8s ease-in-out infinite;
  filter: blur(6px);
  z-index: -1;
}
.trophy-glow::after {
  content: '';
  position: absolute;
  inset: 10%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.85), transparent 60%);
  animation: trophyGlowPulse 1.4s ease-in-out infinite 0.3s reverse;
  filter: blur(3px);
}
@keyframes trophyGlowPulse {
  0%,
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.85;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 1;
  }
}

.result-icon {
  width: 190px;
  height: 190px;
  margin-bottom: 6px;
  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.6)) drop-shadow(0 0 30px rgba(255, 215, 0, 0.7));
  animation: trophyBounce 1.4s ease-in-out infinite;
  position: relative;
  z-index: 3;
}
.trophy-icon {
  width: 100%;
  height: 100%;
}
@keyframes trophyBounce {
  0%,
  100% {
    transform: translateY(0) rotate(-5deg);
  }
  50% {
    transform: translateY(-14px) rotate(5deg);
  }
}

.result-congrats {
  font-size: 2.8rem;
  font-weight: 900;
  letter-spacing: 3px;
  margin: 0 0 6px;
  background: linear-gradient(180deg, #fff5a0 0%, #ffd700 50%, #ff7b00 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.7));
}

.result-subtitle {
  font-size: 1.25rem;
  color: #ffe27a;
  font-weight: 700;
  letter-spacing: 1px;
  margin: 0 0 24px;
  text-shadow: 0 0 8px rgba(255, 200, 0, 0.7), 0 2px 4px rgba(0, 0, 0, 0.7);
}

.reward-amount-wrap {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 10px;
  padding: 22px 46px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, rgba(0, 0, 0, 0.35) 100%);
  border: 4px solid #ffd700;
  border-radius: 22px;
  box-shadow: inset 0 2px 0 rgba(255, 255, 255, 0.35), 0 0 30px rgba(255, 215, 0, 0.7),
    0 0 60px rgba(255, 160, 50, 0.45), 0 10px 30px rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(8px);
  margin-bottom: 36px;
  animation: rewardShine 2.2s ease-in-out infinite;
  position: relative;
  overflow: hidden;
}
.reward-amount-wrap::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 40%;
  height: 200%;
  background: linear-gradient(
    115deg,
    transparent 30%,
    rgba(255, 255, 255, 0.55) 50%,
    transparent 70%
  );
  transform: translateX(-120%) rotate(18deg);
  animation: shineSweep 2.4s ease-in-out infinite;
}
@keyframes rewardShine {
  0%,
  100% {
    filter: brightness(1);
  }
  50% {
    filter: brightness(1.2);
  }
}
@keyframes shineSweep {
  0% {
    transform: translateX(-120%) rotate(18deg);
  }
  60%,
  100% {
    transform: translateX(320%) rotate(18deg);
  }
}
.reward-amount {
  font-size: 5rem;
  font-weight: 900;
  line-height: 1;
  background: linear-gradient(180deg, #fff5a0 0%, #ffd700 45%, #c97c00 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-family: 'Impact', 'Oswald', sans-serif;
  letter-spacing: 2px;
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.7));
}
.reward-unit {
  font-size: 2.4rem;
  font-weight: 900;
  color: #ffb84d;
  margin-top: 6px;
  text-shadow: 0 0 10px rgba(255, 150, 0, 0.9), 0 3px 6px rgba(0, 0, 0, 0.8);
}

.result-actions {
  display: flex;
  gap: 14px;
}
.btn-play-again {
  padding: 14px 36px;
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: 2px;
  color: #4a0000;
  background: linear-gradient(180deg, #fff1a8 0%, #ffd700 50%, #c99400 100%);
  border: 3px solid #8b6508;
  border-radius: 14px;
  cursor: pointer;
  box-shadow: inset 0 2px 0 rgba(255, 255, 255, 0.6), inset 0 -4px 0 rgba(0, 0, 0, 0.2),
    0 0 20px rgba(255, 215, 0, 0.5), 0 7px 0 #5a3d00, 0 10px 22px rgba(0, 0, 0, 0.55);
  transition: all 0.12s ease;
}
.btn-play-again:hover {
  transform: translateY(-2px);
  box-shadow: inset 0 2px 0 rgba(255, 255, 255, 0.6), inset 0 -4px 0 rgba(0, 0, 0, 0.2),
    0 0 30px rgba(255, 215, 0, 0.75), 0 9px 0 #5a3d00, 0 13px 26px rgba(0, 0, 0, 0.55);
}
.btn-play-again:active {
  transform: translateY(5px);
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.35), 0 0 16px rgba(255, 215, 0, 0.5), 0 2px 0 #5a3d00,
    0 4px 10px rgba(0, 0, 0, 0.5);
}

.animate-result-in {
  animation: resultIn 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
@keyframes resultIn {
  0% {
    opacity: 0;
    transform: scale(0.6) translateY(40px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@media (max-width: 480px) {
  .heading-mid {
    font-size: 1.8rem;
    letter-spacing: 3px;
  }
  .heading-top,
  .heading-bot {
    font-size: 1rem;
  }
  .input-wrap {
    width: 280px;
  }
  .code-input {
    padding: 14px 16px;
    font-size: 1.05rem;
  }
  .code-input-spin {
    font-size: 1rem;
    padding: 12px 16px;
  }
  .result-congrats {
    font-size: 2.2rem;
  }
  .reward-amount {
    font-size: 3.8rem;
  }
  .reward-unit {
    font-size: 2rem;
  }
  .btn-spin {
    padding: 14px 46px;
    font-size: 1.3rem;
    letter-spacing: 2px;
  }
  .sector-text {
    font-size: 13px;
    stroke-width: 2.5px;
  }
  .code-section {
    gap: 14px;
    margin-top: 20px;
  }
}
</style>
